import { Box } from '@mui/material';
import { type ReactElement, useEffect, useState } from 'react';

import InputSearch from '../../../InputSearch/InputSearch';
import { SlabDraggableList } from '../../../SlabDraggableList/SlabDraggableList';
import type { Column } from '../../TableDataModel';
import { ColumnItem } from './ColumnItem';

type ColumnConfigurationPopoverProps<TRow extends { id: string }> = {
  columns: Column<TRow>[];
  renderedColumns: Column<TRow>[];
  onColumnChange: (columnID: string, isRendered: boolean) => void;
  onOrderChange: (newColumns: Column<TRow>[]) => void;
};

export const ColumnConfigurationForm = <TRow extends { id: string }>({
  columns,
  renderedColumns,
  onColumnChange,
  onOrderChange,
}: ColumnConfigurationPopoverProps<TRow>): ReactElement => {
  const [colSearchString, setColSearchString] = useState('');
  const [localColumns, setLocalColumns] = useState<Column<TRow>[]>(columns);

  // Sync when columns are changed, so the local reorder that hasn't propagated back yet doesn't get overwritten.
  const columnIDs = (cs: Column<TRow>[]): string => cs.map((c) => c.id).join(',');
  useEffect(() => {
    if (columnIDs(columns) !== columnIDs(localColumns)) {
      setLocalColumns(columns);
    }
  }, [columns]);

  const filteredColumns =
    colSearchString === ''
      ? localColumns
      : localColumns.filter((col) =>
          (typeof col.label === 'function' ? col.label([]) : col.label)
            .toLowerCase()
            .includes(colSearchString.toLowerCase()),
        );
  const disableDnD = colSearchString.trim() !== '';

  return (
    <Box
      sx={{
        padding: '0.625rem',
        backgroundColor: 'white',
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      <InputSearch onChange={(v): void => setColSearchString(v)} placeholder='Search' />
      <SlabDraggableList
        sx={{ paddingTop: '1rem' }}
        data={filteredColumns}
        keyExtractor={(item) => item.id}
        onReorder={({ reordered }) => {
          setLocalColumns(reordered);
          onOrderChange(reordered);
        }}
        droppableId='dnd'
        direction='vertical'
        renderItem={({ item: col, dragControls }) => (
          <ColumnItem
            disableDnD={disableDnD}
            col={col}
            isChecked={renderedColumns.some((c) => c.id === col.id)}
            dragControls={dragControls}
            onColumnChange={onColumnChange}
          />
        )}
      />
    </Box>
  );
};
